import '../../styles/AssetLibrary.css';

import {
  AssetMetadata,
  AssetOwnerCategory,
  AssetType
} from '../../models/Asset';
import {
  AssetUploading,
  VideoEncodeObservable,
  useAssetStorage
} from '../../hooks/useAssetStorage';
import { FormControl, InputLabel, MenuItem } from '@mui/material';
import React, { useEffect, useState } from 'react';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import Slide, { SlideProps } from '@mui/material/Slide';
import { cloneDeep, isEmpty } from 'lodash';

import AlertSnackbar from '../AlertSnackbar';
import AssetService from '../../services/AssetService';
import AssetUploadDialog from '../AssetUploadDialog';
import { AssetsPanel } from './AssetsPanel';
import AuthService from '../../services/AuthService';
import Button from '@mui/material/Button';
import CloseIcon from '@mui/icons-material/Close';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import ConfirmationDialog from '../ConfirmationDialog';
import { CustomLoadingProgress } from '../../../shared/components/CustomLoadingProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Grid from '@mui/material/Unstable_Grid2';
import IconButton from '@mui/material/IconButton';
import SearchTextField from '../SearchTextField';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Tooltip from '@mui/material/Tooltip';
import { VIDEO_ENCODING_COMPLETION_CHECK_INTERVAL } from '../../constants';
import useAssets from '../../hooks/useAssets';
import { useVideoEncoder } from '../../hooks/useVideoEncoder';

const Transition = React.forwardRef(
  (props: SlideProps, ref: React.Ref<unknown>) => (
    <Slide direction="up" ref={ref} {...props} />
  )
);

interface AssetLibraryProps {
  title?: string;
  types: AssetType[];
  category: AssetOwnerCategory;
  selectedAssetkey?: string;
  thumbnailWidth?: number;
  thumbnailHeight?: number;
  onSelect: (assetkey: string) => void;
  onClose: () => void;
}

type FilterOption = 'authorFiles' | 'allFiles';
type SortOption = 'newest' | 'oldest' | 'ascending' | 'descending';

const AssetLibrary = (props: AssetLibraryProps) => {
  const [open, setOpen] = React.useState(false);
  const [assets, setAssets] = useState<AssetUploading[]>([]);
  const [deletingAsset, setDeletingAsset] = useState(false);
  const [assetToDelete, setAssetToDelete] = useState<AssetMetadata>();
  const [selectedAssetkey, setSelectedAssetkey] = useState<string>(
    props.selectedAssetkey
  );
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [searchText, setSearchText] = useState('');
  const [sortBy, setSortBy] = useState<SortOption>('newest');
  const [filter, setFilter] = React.useState<FilterOption>('authorFiles');
  const [authorLazyLoad, setAuthorLazyLoad] = useState<number>(0); // To keep individual LazyLoad for Author and All Files
  const [allFilesLazyLoad, setAllFilesLazyLoad] = useState<number>(0); // To keep individual LazyLoad for Author and All Files
  const {
    assetsLoading,
    assetsError,
    assetsData,
    deleteAssets,
    deletingAssets,
    deleteAssetsError,
    deleted
  } = useAssets(props.types, props.category, true /* load assets */);

  const { requestEncoding } = useVideoEncoder(AuthService.Tenant);

  const { upload } = useAssetStorage(AuthService.Tenant);

  useEffect(() => {
    if (
      assetsData &&
      (assets == null || assetsData.assets.length !== assets.length)
    ) {
      const copy = assetsData.assets.map(
        (asset) =>
          ({ metadata: asset, uploadObservable: null } as AssetUploading)
      );
      setAssets(copy);
    }
  }, [assetsData]);

  function getAcceptFileTypes(types: AssetType[]) {
    const acceptTypes: string[] = [];

    if (types.includes('image')) {
      acceptTypes.push('image/*');
    }
    if (types.includes('video')) {
      acceptTypes.push('video/*');
    }

    if (types.includes('audio')) {
      acceptTypes.push('audio/*');
    }

    if (types.includes('pdf')) {
      acceptTypes.push('application/pdf');
    }
    if (types.includes('document')) {
      // TODO: Instead of accepting *all* types, include the same ones as are
      // allowed by the File Uploader component.
      for (; acceptTypes.length; acceptTypes.pop());
    }

    return acceptTypes.join(',');
  }

  function handleUploadClick() {
    setShowUploadDialog(true);
  }

  async function handleUpload(files: File[]) {
    setShowUploadDialog(false);
    const assetsUploading = await upload(
      files,
      props.category === 'author' ? 'author_assets' : 'learner_assets'
    );
    setAssets((value) => {
      const copy = [...value];
      copy.unshift(...assetsUploading);
      return copy;
    });
  }

  async function handleUploadCompletedAsync(assetkey: string) {
    setAssets((value) => {
      const copy = [...value];
      const asset = copy.find((a) => a.metadata.assetkey === assetkey);
      if (asset) {
        asset.abortController = null;
        asset.uploadObservable = null;
        if (asset.metadata.contentType.startsWith('video/')) {
          asset.encodeObservable = startVideoEncodingAsync(assetkey);
        }
      }
      return copy;
    });
  }

  function startVideoEncodingAsync(assetkey: string) {
    const owner = AuthService.Userkey;
    requestEncoding(AuthService.Userkey, assetkey, props.category);

    return new VideoEncodeObservable((subscriber) => {
      const timerId = window.setInterval(async () => {
        try {
          //* Check if the metadata of the video uploaded is available periodically.
          const metadata = await AssetService.getAssetMetadataAsync(
            AuthService.Tenant,
            props.category,
            owner,
            assetkey
          );
          subscriber.next(metadata);
          subscriber.complete();
        } catch (error) {
          if (error.response.status === 404) {
            //* Encoding is still going, just swallow the error.
            return;
          }
          subscriber.error(error);
          throw error;
        }
      }, VIDEO_ENCODING_COMPLETION_CHECK_INTERVAL);
      subscriber.next(timerId);
    });
  }

  function handleUploadCanceled(assetkey: string) {
    removeAsset(assetkey);
  }

  function handleUploadError(assetkey: string) {
    if (!errorMessage) {
      setErrorMessage('There was an error while uploading files.');
    }

    removeAsset(assetkey);
  }

  function handleEncodeCompleted(assetMetadata: AssetMetadata) {
    setAssets((value) => {
      const copy = [...value];
      const asset = copy.find(
        (a) => a.metadata.assetkey === assetMetadata.assetkey
      );
      if (asset) {
        asset.metadata = { ...assetMetadata };
        asset.abortController = null;
        asset.uploadObservable = null;
        asset.encodeObservable = null;
      }
      return copy;
    });
  }

  function handleEncodeError(assetkey: string) {
    if (!errorMessage) {
      setErrorMessage('There was an error while encoding the uploaded video.');
    }

    removeAsset(assetkey);
  }

  function removeAsset(assetkey: string) {
    setAssets((value) => {
      const copy = [...value]; //* Not deep-cloning to keep the references of the abortController and the observable.
      const assetIndex = copy.findIndex(
        (a) => a.metadata.assetkey === assetkey
      );
      if (assetIndex >= 0) {
        const asset = copy[assetIndex];
        asset.abortController.abort();
        asset.abortController = null;
        asset.uploadObservable = null;
      }
      copy.splice(assetIndex, 1);
      return copy;
    });
  }

  function handleCloseUploadDialog() {
    setShowUploadDialog(false);
  }

  function handleClick(assetkey: string, endSelection?: boolean) {
    setSelectedAssetkey(assetkey);
    if (endSelection) {
      props.onSelect(assetkey);
      props.onClose();
    }
  }

  function handleDelete(asset: AssetMetadata) {
    setAssetToDelete(asset);
    setOpen(true);
  }

  function handleDoDelete(agreed: boolean, asset: AssetMetadata) {
    setDeletingAsset(false);
    if (!agreed) {
      setOpen(false);
      return;
    }
    const assetkeys: string[] = [asset.assetkey];
    //* If the asset is a video, delete its thumbnail as well
    if (asset.contentType.startsWith('video/')) {
      assetkeys.push(AssetService.getVideoThumbnailAssetkey(asset.assetkey));
    }
    deleteAssets(assetkeys, props.category);
    setAssetToDelete(null);
    setOpen(false);
  }

  useEffect(() => {
    if (isEmpty(deleted)) {
      return;
    }
    setAssets((value) => {
      const copy = cloneDeep(value);
      for (const deletedAssetkey of deleted) {
        const index = copy.findIndex(
          (a) => a.metadata.assetkey === deletedAssetkey
        );
        if (index >= 0) {
          copy.splice(index, 1);
        }
      }
      return copy;
    });
  }, [deleted]);

  useEffect(() => {
    if (!!deleteAssetsError && !errorMessage) {
      setErrorMessage('There was an error while deleting assets.');
    }
  }, [deleteAssetsError]);

  function handleSelect() {
    if (selectedAssetkey) {
      props.onSelect(selectedAssetkey);
      props.onClose();
    }
  }

  function handleSearch(value: string) {
    setSearchText(value);
  }

  const handleLazyLoad = (filterType: FilterOption) => {
    if (filterType === 'authorFiles') {
      setAuthorLazyLoad(authorLazyLoad + 1);
    } else {
      setAllFilesLazyLoad(allFilesLazyLoad + 1);
    }
  };

  const handleFilterBy = (filterType: FilterOption) => {
    setFilter(filterType);
    handleLazyLoad(filterType);
  };

  const handleSortSelection = (event: SelectChangeEvent<SortOption>) => {
    setSortBy(event.target.value as SortOption);
    handleSorting(event.target.value as SortOption);
    handleLazyLoad(filter);
  };

  const ascending = (property: string, assetsToSort: AssetUploading[]) => {
    assetsToSort.sort((a, b) => {
      if (a.metadata[property] > b.metadata[property]) {
        return 1;
      }
      if (a.metadata[property] < b.metadata[property]) {
        return -1;
      }
      return 0;
    });
  };

  const descending = (property: string, assetsToSort: AssetUploading[]) => {
    assetsToSort.sort((a, b) => {
      if (a.metadata[property] > b.metadata[property]) {
        return -1;
      }
      if (a.metadata[property] < b.metadata[property]) {
        return 1;
      }
      return 0;
    });
  };

  const handleSorting = (value: string, assets?: AssetUploading[]) => {
    const assetsToSort = assets || filteredAssets;
    switch (value) {
      case 'newest':
        descending('dateCreated', assetsToSort);
        break;
      case 'oldest':
        ascending('dateCreated', assetsToSort);
        break;
      case 'ascending':
        ascending('filename', assetsToSort);
        break;
      case 'descending':
        descending('filename', assetsToSort);
        break;
      default:
        descending('dateCreated', assetsToSort);
        break;
    }
  };

  const filterBy = (asset: AssetUploading): boolean => {
    if (filter === 'authorFiles') {
      const userkey = AuthService.Userkey;
      return asset.metadata.owner === userkey;
    }
    return true;
  };

  const searchBy = (asset: AssetUploading): boolean => {
    return asset.metadata.filename
      .toLowerCase()
      .includes(searchText.toLowerCase());
  };

  const filteredAssets = assets.filter(filterBy).filter(searchBy);

  handleSorting(sortBy, filteredAssets);

  return (
    <>
      <Dialog open fullScreen TransitionComponent={Transition}>
        <DialogTitle>
          <Grid container direction="column">
            <Grid>
              <Grid container spacing={2}>
                <Grid>
                  <span>{props.title || 'Asset Library'}</span>
                </Grid>
                <Grid>
                  <Button
                    variant="contained"
                    size="small"
                    color="primary"
                    startIcon={<CloudUploadIcon />}
                    onClick={handleUploadClick}
                  >
                    Upload
                  </Button>
                </Grid>
              </Grid>

              <div className="close-button">
                <Tooltip
                  title={`Close ${props.title || 'Asset Library'}`}
                  placement="left"
                >
                  <IconButton onClick={props.onClose}>
                    <CloseIcon />
                  </IconButton>
                </Tooltip>
              </div>
            </Grid>
            <Grid>
              <Grid container className="searchFilterGrid">
                <Grid>
                  <SearchTextField
                    disabled={!filteredAssets}
                    trigger="type"
                    style={{ width: '200px', margin: '8px 0px' }}
                    onSearch={handleSearch}
                  />
                </Grid>
                <Grid className="marginFilter">
                  <FormControl variant="outlined" margin="dense" size="small">
                    <InputLabel htmlFor="filter-media-label">
                      Sort by
                    </InputLabel>
                    <Select
                      labelId="filter-media-label"
                      id="filter-media"
                      value={sortBy}
                      variant="outlined"
                      label="Filter by"
                      className="filter"
                      onChange={handleSortSelection}
                    >
                      <MenuItem value={'newest'}>Date: Newest</MenuItem>
                      <MenuItem value={'oldest'}>Date: Oldest</MenuItem>
                      <MenuItem value={'ascending'}>Name: Ascending</MenuItem>
                      <MenuItem value={'descending'}>Name: Descending</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid className="marginFilter">
                  <FormControl variant="outlined" margin="dense" size="small">
                    <ToggleButtonGroup value={filter} exclusive>
                      <ToggleButton
                        size="small"
                        value="authorFiles"
                        color="primary"
                        className="toggleButton"
                        onClick={() => handleFilterBy('authorFiles')}
                      >
                        My Files
                      </ToggleButton>
                      <ToggleButton
                        size="small"
                        value="allFiles"
                        color="primary"
                        className="toggleButton"
                        onClick={() => handleFilterBy('allFiles')}
                      >
                        All Files
                      </ToggleButton>
                    </ToggleButtonGroup>
                  </FormControl>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </DialogTitle>

        <DialogContent>
          {(assetsLoading || deletingAssets) && <CustomLoadingProgress />}
          {assetsLoading && isEmpty(filteredAssets) ? (
            <></>
          ) : isEmpty(filteredAssets) ? (
            <p style={{ textAlign: 'center' }}>
              There are no assets available.
            </p>
          ) : (
            <AssetsPanel
              thumbnailWidth={props.thumbnailWidth}
              thumbnailHeight={props.thumbnailHeight}
              category={props.category}
              lazyLoad={
                filter === 'authorFiles'
                  ? authorLazyLoad < 1 // By default library is loaded with 'Author Files' and 'Lazy load' enabled.
                  : // So we can disable 'Lazy load' next time
                    allFilesLazyLoad < 2
              }
              selectedAssetkey={selectedAssetkey}
              assetFiles={filteredAssets}
              assetsError={assetsError}
              onUploadCompleted={handleUploadCompletedAsync}
              onUploadCanceled={handleUploadCanceled}
              onUploadError={handleUploadError}
              onEncodeCompleted={handleEncodeCompleted}
              onEncodeError={handleEncodeError}
              handleClick={handleClick}
              handleDelete={handleDelete}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" color="primary" onClick={props.onClose}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            disabled={selectedAssetkey === null}
            onClick={handleSelect}
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>
      {assetToDelete && (
        <ConfirmationDialog
          open={open}
          title={`Are you sure you want to delete asset`}
          message={`This operation isn't reversible.`}
          onClose={(agreed: boolean) => {
            handleDoDelete(agreed, assetToDelete);
          }}
        />
      )}
      {showUploadDialog && (
        <AssetUploadDialog
          accept={getAcceptFileTypes(props.types)}
          multiple={true}
          onUpload={handleUpload}
          onClose={handleCloseUploadDialog}
        />
      )}
      <AlertSnackbar
        open={!!errorMessage}
        message={errorMessage}
        severity="error"
        onClose={() => setErrorMessage('')}
      />
    </>
  );
};

export default AssetLibrary;
